import _ from 'lodash';
import type { MuiCityMapper, MuiTransportModeMapper } from '../config/mappers';
import type { MuiFareProfileOptionMapper } from '../config/mappers/MuiFareProfileOptionMapper';
import { TicketPickerFacade } from '../facades/TicketPickerFacade';
import { MapperFactory } from '../mappers/MapperFactory';
import type { GetTicketsInputType } from '../types/facades/ITicketPickerFacade';
import type { MuiCityOption } from '../types/forms/MuiCityOption';
import type { MuiFareProfileOption } from '../types/forms/MuiFareProfileOption';
import type { MuiTransportModeOption } from '../types/forms/MuiTransportModeOption';
import type { Tickets } from '../types/models/Tickets';
import type { GetCityListMuiAutocompleteInputType, GetTicketsModuleInputType, ITicketPickerModule } from '../types/modules/ITicketPickerModule';
import type { TicketsViewModel } from '../types/viewmodels/TicketsViewModel';
import { CardSupportViewModel } from '../types/viewmodels/CardSupportViewModel';

export class TicketPickerModule implements ITicketPickerModule {
    private _apiClient;
    private _ticketPickerFacade;

    //eslint-disable-next-line
    constructor(apiClient: any) {
        this._apiClient = apiClient;
        this._ticketPickerFacade = new TicketPickerFacade(this._apiClient);
    }
    
    async getCardSupportList(): Promise<CardSupportViewModel[]> {
        const cardSupportList = await this._ticketPickerFacade.getCardSupportList();
        return cardSupportList;
    }

    async getTransportModeList(mapper: string): Promise<MuiTransportModeOption[]> {
        const _mapper = new MapperFactory().build(mapper) as MuiTransportModeMapper;
        const transportModeList = _mapper.mapToView(await this._ticketPickerFacade.getTransportModeList());
        if (!Array.isArray(transportModeList)) {
            return [transportModeList];
        }
        return transportModeList;
    }

    async getCityList(params: GetCityListMuiAutocompleteInputType, mapper: string): Promise<MuiCityOption[]> {
        const _mapper = new MapperFactory().build(mapper) as MuiCityMapper;
        const cityList = _mapper.mapToView(await this._ticketPickerFacade.getCityList(params));
        if (!Array.isArray(cityList)) {
            return [cityList];
        }
        return cityList;
    }

    async getFareProfileList(mapper: string): Promise<MuiFareProfileOption[]> {
        const _mapper = new MapperFactory().build(mapper) as MuiFareProfileOptionMapper;
        const fareProfileList = _mapper.mapToView(await this._ticketPickerFacade.getFareProfileList());
        if (!Array.isArray(fareProfileList)) {
            return [fareProfileList];
        }
        return fareProfileList;
    }

    async getTickets(params: GetTicketsModuleInputType): Promise<TicketsViewModel> {
        const input: GetTicketsInputType = {
            cityOriginCode: params.departureCityCode,
            cityDestinationCode: params.arrivalCityCode,
            transportModeCode: params.transportModeCode,
            fareProfileCode: params.fareProfileCode,
        };
        const tickets = await this._ticketPickerFacade.getTickets(input);
        const orderedTickets: Tickets = {
            ...tickets,
            messageList: _.orderBy(tickets.messageList, ['priorityOrder']),
            ticketPackageList: _.orderBy(tickets.ticketPackageList, ['priorityOrder']),
        };

        return orderedTickets;
    }
}
